/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/GutterAdornment" />
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { IGutterAdornment } from "../../Contracts/PullRequestAdornments";

let adornmentIdCount = 0;

export function getGutterAdornmentWidgetRegistration(adornments: IGutterAdornment[]): IEditorWidgetRegistration<GutterAdornmentWidget> {
    const widgetId = "gutter-adornment-" + adornmentIdCount++;
    return {
        addWidget: (editor, diffEditor) => {
            let isOriginalDiff = false;
            if (diffEditor) {
                isOriginalDiff = diffEditor.getOriginalEditor() === editor;
            }

            const linkWidget = new GutterAdornmentWidget(
                editor,
                widgetId,
                adornments.filter(a => a.isOriginalSide === isOriginalDiff)
            );
            editor.addOverlayWidget(linkWidget);
            return linkWidget;
        },
        removeWidget: (widget, editor) => {
            widget.dispose();
            editor.removeOverlayWidget(widget);
        },
        widgetId
    };
}

const gutterIconWidth = 16;

class GutterAdornmentWidget implements monaco.editor.IOverlayWidget {
    private adornmentCollection: { [id: string]: IGutterAdornment } = {};
    private adornmentElementCollection: { [id: string]: HTMLDivElement } = {};
    private disposables: monaco.IDisposable[] = [];
    private domNode: HTMLElement;
    private editor: monaco.editor.ICodeEditor;
    private editorFontInfo: monaco.editor.BareFontInfo;
    private id: string;
    private lineNumberWidth: number;

    constructor(editor: monaco.editor.ICodeEditor, widgetId: string, adornments: IGutterAdornment[]) {
        this.editor = editor;
        this.id = widgetId;

        this.editorFontInfo = this.editor.getOption(monaco.editor.EditorOption.fontInfo);
        this.lineNumberWidth = this.editor.getLayoutInfo().lineNumbersWidth;

        // onDidChangeViewZones is not a part of the Monaco.Editor.d.ts declaration but is part of the monaco API
        this.disposables.push((this.editor as any).onDidChangeViewZones(this.updateAdornments));

        this.disposables.push(
            this.editor.onDidScrollChange((e: monaco.IScrollEvent) => {
                const scrollTop = e.scrollTop || 0;
                const top = this.editor.getTopForLineNumber(0) - scrollTop;
                this.getDomNode().style.top = top.toString() + "px";
            })
        );

        this.disposables.push(
            this.editor.onDidLayoutChange((e: monaco.editor.EditorLayoutInfo) => {
                if (e.lineNumbersWidth !== this.lineNumberWidth) {
                    this.lineNumberWidth = e.lineNumbersWidth;
                    this.updateAdornmentsLayout();
                }
            })
        );

        for (const adornment of adornments) {
            this.addAdornment(adornment);
        }
    }

    public getId(): string {
        return this.id;
    }

    public dispose() {
        this.disposables.forEach(d => d.dispose());
        this.disposables = [];
    }

    public getDomNode() {
        if (!this.domNode) {
            this.domNode = document.createElement("div");
            this.domNode.className = "pr-gutter-adornment-container";
        }
        return this.domNode;
    }

    public getPosition() {
        return null;
    }

    private addAdornment(adornment: IGutterAdornment) {
        const startLine = adornment.startLine > 0 ? adornment.startLine : 1;
        const endLine = adornment.endLine;

        if (startLine < endLine) {
            let adornmentHeight = this.editor.getTopForLineNumber(endLine) - this.editor.getTopForLineNumber(startLine);

            if (endLine > this.editor.getModel()!.getLineCount()) {
                adornmentHeight += this.editorFontInfo.lineHeight;
            }

            // Create dom node for adornment
            const topForLineNumber = this.editor.getTopForLineNumber(adornment.startLine);

            const nodeStyle = `position: absolute; top: ${topForLineNumber}px; 
            line-height: ${this.editorFontInfo.lineHeight}px; 
            font-family: ${this.editorFontInfo.fontFamily}; font-size:${this.editorFontInfo.fontSize}px;
            font-weight: ${this.editorFontInfo.fontWeight}; letter-spacing: ${this.editorFontInfo.letterSpacing};
            width: ${this.lineNumberWidth + gutterIconWidth}px; text-align: right; height: ${adornmentHeight.toString()}px;
            visibility: ${this.lineNumberWidth > 0 ? "visible" : "hidden"}`;

            const adornmentNode = document.createElement("div");
            adornmentNode.className = adornment.className;
            adornmentNode.setAttribute("style", nodeStyle);

            // Create line number container
            const lineNumberContainer = document.createElement("div");
            lineNumberContainer.className = "line-number-container";
            this.appendAdornmentLineNumbers(startLine, endLine, lineNumberContainer);
            adornmentNode.appendChild(lineNumberContainer);

            // If helptext is not empty then show tool tip host
            if (adornment.helpText) {
                adornmentNode.setAttribute("title", adornment.helpText);
            }

            // Append to main container
            this.getDomNode().appendChild(adornmentNode);

            // cache adornment
            this.adornmentCollection[adornment.id] = adornment;

            // remove element if there was a conflict in cache
            if (this.adornmentElementCollection[adornment.id]) {
                this.adornmentElementCollection[adornment.id].remove();
            }

            // cache adornment node
            this.adornmentElementCollection[adornment.id] = adornmentNode;
        }
    }

    /**
     * Gutter width can change when content loads
     * For example before the file has been loaded the default width is 33px
     * If content has more than 99 lines, the gutter width adjusts to account for the 3rd char
     */
    private updateAdornmentsLayout() {
        for (const key in this.adornmentElementCollection) {
            if (this.lineNumberWidth > 0) {
                this.adornmentElementCollection[key].style.width = this.lineNumberWidth + gutterIconWidth + "px";
                this.adornmentElementCollection[key].style.visibility = "visible";
            } else {
                this.adornmentElementCollection[key].style.visibility = "hidden";
            }
        }
    }

    /**
     * Needs to be called whenever there is a change in viewzone
     **/
    private updateAdornments = () => {
        for (const key in this.adornmentElementCollection) {
            const adornment = this.adornmentCollection[key];
            const adornmentNode = this.adornmentElementCollection[key];

            const startLine = adornment.startLine > 0 ? adornment.startLine : 1;
            const endLine = adornment.endLine;

            const startLineTop = this.editor.getTopForLineNumber(startLine);

            adornmentNode.style.top = startLineTop + "px";
            adornmentNode.style.height = this.editor.getTopForLineNumber(endLine - 1) - startLineTop + this.editorFontInfo.lineHeight + "px";

            const lineNumberContainer = adornmentNode.querySelector(".line-number-container") as HTMLDivElement;
            if (lineNumberContainer) {
                lineNumberContainer.innerHTML = "";
                this.appendAdornmentLineNumbers(startLine, endLine, lineNumberContainer);
            }
        }
    };

    private appendAdornmentLineNumbers(startLine: number, endLine: number, container: HTMLElement) {
        for (let i = startLine, prevTop; i < endLine; i++) {
            const top = this.editor.getTopForLineNumber(i);

            if (prevTop && prevTop + this.editorFontInfo.lineHeight !== top) {
                // append empty space between lines to adjust for view zones
                const spacer = document.createElement("div");
                spacer.className = "flex-grow";

                container.append(spacer);
                container.className = "flex-column";
                container.style.height = "100%";
            }

            const lineElement = document.createElement("div");
            lineElement.style.height = this.editorFontInfo.lineHeight + "px";
            lineElement.innerHTML = i.toString();
            container.appendChild(lineElement);

            prevTop = top;
        }
    }
}
